import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SERVICES, INDUSTRIES } from "@/data/content";
import { WHAT } from "@/constants/testIds";

const PROCESS = [
    { step: "01", title: "Audit", copy: "We map your funnel end-to-end across paid, organic, CRM, creative and compliance, and benchmark against category leaders." },
    { step: "02", title: "Strategy", copy: "A 90-day acquisition plan with channel mix, creative direction, KPI targets and a tracking architecture you actually trust." },
    { step: "03", title: "Build", copy: "Creative production, landing pages, automations and tagging, all assembled by the team that will run them." },
    { step: "04", title: "Activate", copy: "Hands-on media buying with daily optimisation, weekly performance reviews and quarterly strategic resets." },
    { step: "05", title: "Compound", copy: "We stay in the cockpit. Most engagements turn into multi-year partnerships measured in LTV, not lift charts." }
];

export default function WhatWeDo() {
    return (
        <div data-testid={WHAT.section}>
            {/* HERO */}
            <section className="container-x pt-24 pb-20">
                <p className="overline mb-8">Chapter 02 · What we do</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.9] tracking-tighter"
                        style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                    >
                        Full-service{" "}
                        <span className="italic text-brand-primary">growth engineering.</span>
                    </h1>
                </Reveal>
                <Reveal delay={120} className="mt-12 max-w-3xl">
                    <p className="text-xl text-brand-frost/90 leading-relaxed">
                        Twelve capabilities, designed to work as one. We don&apos;t hand you off between teams. Strategy, creative, media and CRM sit in the same room, on the same KPI sheet.
                    </p>
                </Reveal>
            </section>

            {/* SERVICES, long editorial list */}
            <section className="border-t border-brand-primary/10">
                <div className="container-x">
                    {SERVICES.map((s, i) => (
                        <Reveal
                            key={s.slug}
                            data-testid={WHAT.serviceItem(s.slug)}
                            delay={i * 40}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-16 lg:py-24 border-b border-brand-primary/10 group"
                        >
                            <div className="lg:col-span-2">
                                <span className="font-mono text-sm text-brand-primary/60 tracking-widest">{s.number}</span>
                            </div>
                            <div className="lg:col-span-5">
                                <h2 className="font-display h-display-md text-brand-mist transition-colors group-hover:text-brand-primary">
                                    {s.title}
                                </h2>
                            </div>
                            <div className="lg:col-span-5 space-y-6">
                                <p className="text-lg text-brand-frost/85 leading-relaxed">{s.excerpt}</p>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm text-brand-primary/90">
                                    {s.deliverables.map((d) => (
                                        <li key={d} className="flex items-start gap-3">
                                            <span
                                                aria-hidden="true"
                                                className="mt-[0.55em] inline-block h-1.5 w-1.5 rounded-full bg-brand-primary shrink-0"
                                            />
                                            <span>{d}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="pt-2">
                                    <Link
                                        to={`/snapshot#${s.snapshotAnchor}`}
                                        data-testid={`see-snapshot-${s.slug}`}
                                        className="group/link inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-mist border border-brand-primary/40 px-4 py-2.5 hover:bg-brand-primary hover:text-brand-bg transition-colors duration-300"
                                    >
                                        See Snapshot
                                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                                    </Link>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 lg:py-32 bg-brand-surface/40 border-y border-brand-primary/10">
                <div className="container-x">
                    <Reveal className="mb-20 max-w-3xl">
                        <p className="overline mb-6">Engagement model</p>
                        <h2 className="font-display h-display-lg">
                            How we
                            <br />
                            <span className="italic text-brand-primary">run a partnership.</span>
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-brand-primary/10">
                        {PROCESS.map((p, i) => (
                            <Reveal key={p.step} delay={i * 80} className="bg-brand-bg p-6 sm:p-8 lg:p-10 flex flex-col">
                                <span className="font-mono text-xs text-brand-primary/60 mb-8">{p.step}</span>
                                <h3 className="font-display text-2xl text-brand-mist mb-4 tracking-tight">{p.title}</h3>
                                <p className="text-sm text-brand-frost/75 leading-relaxed">{p.copy}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section className="py-24 lg:py-32 border-b border-brand-primary/10">
                <div className="container-x">
                    <Reveal className="mb-16 max-w-4xl">
                        <p className="overline mb-6">Where we deploy</p>
                        <h2 className="font-display h-display-md">
                            Specialised for the verticals that demand it.
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {INDUSTRIES.map((ind, i) => (
                            <Reveal key={ind.slug} delay={i * 100} className="border border-brand-primary/15 p-6 sm:p-8 lg:p-10 bg-brand-surface">
                                <p className="overline mb-6">{ind.kicker}</p>
                                <h3 className="font-display h-display-sm text-brand-mist mb-4">{ind.title}</h3>
                                <p className="text-brand-frost/80 leading-relaxed">{ind.copy}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32">
                <div className="container-x flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
                    <Reveal>
                        <h2 className="font-display h-display-lg max-w-3xl">
                            Tell us what you&apos;re scaling.
                            <br />
                            <span className="italic text-brand-primary">We&apos;ll tell you how.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={120}>
                        <Link to="/contact" className="btn-primary">
                            Start a project <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
